import React from 'react'
import { connect } from 'react-redux'
import { Card, Button, Table, Modal, Input, message, Icon, Alert, Divider, Tag } from 'antd'
import { QRCodeSVG } from 'qrcode.react'
import AdminLayout from '../../components/AdminLayout'
import {
  fetchSecurityStatus,
  setupTotp,
  verifyTotp,
  disableTotp,
  fetchPasskeys,
  addPasskey,
  deletePasskey,
} from '../../actions/security'

class SecuritySettings extends React.Component {
  state = {
    totpSetupModalVisible: false,
    totpDisableModalVisible: false,
    passkeyAddModalVisible: false,
    verifyCode: '',
    disableCode: '',
    deviceName: '',
  }

  componentDidMount() {
    this.props.fetchSecurityStatus()
    this.props.fetchPasskeys()
  }

  // TOTP Setup
  handleTotpSetup = () => {
    this.props.setupTotp()
    this.setState({ totpSetupModalVisible: true, verifyCode: '' })
  }

  handleTotpVerify = () => {
    this.props.verifyTotp(this.state.verifyCode)
    this.setState({ totpSetupModalVisible: false, verifyCode: '' })
  }

  // TOTP Disable
  handleTotpDisableClick = () => {
    this.setState({ totpDisableModalVisible: true, disableCode: '' })
  }

  handleTotpDisable = () => {
    this.props.disableTotp(this.state.disableCode)
    this.setState({ totpDisableModalVisible: false, disableCode: '' })
  }

  // Passkey Add
  handlePasskeyAddClick = () => {
    this.setState({ passkeyAddModalVisible: true, deviceName: '' })
  }

  handlePasskeyAdd = async () => {
    try {
      await this.props.addPasskey(this.state.deviceName || 'Security Key')
      this.setState({ passkeyAddModalVisible: false })
      message.success('Passkey added successfully')
    } catch (error) {
      message.error(error.message)
    }
  }

  // Passkey Delete
  handlePasskeyDelete = (passkeyId) => {
    Modal.confirm({
      title: 'Remove Passkey',
      content: 'Are you sure you want to remove this passkey?',
      okType: 'danger',
      onOk: () => this.props.deletePasskey(passkeyId),
    })
  }

  render() {
    const { totpEnabled, passkeyEnabled, passkeys, totpSetupOtpauth, totpSetupSecret, isLoading } = this.props

    const passkeyColumns = [
      { title: 'Device Name', dataIndex: 'device_name', key: 'device_name' },
      {
        title: 'Last Used',
        dataIndex: 'last_used_at',
        key: 'last_used_at',
        render: (val) => val ? new Date(val).toLocaleString() : 'Never',
      },
      {
        title: 'Created',
        dataIndex: 'created_at',
        key: 'created_at',
        render: (val) => new Date(val).toLocaleString(),
      },
      {
        title: 'Action',
        key: 'action',
        render: (_, record) => (
          <Button type='danger' size='small' onClick={() => this.handlePasskeyDelete(record.id)}>
            Remove
          </Button>
        ),
      },
    ]

    return (
      <AdminLayout title='Security Settings'>
        <Card title='Two-Factor Authentication (TOTP)' style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ marginBottom: 8 }}>
                <Icon type='safety' style={{ marginRight: 8 }} />
                Status: {totpEnabled ? <Tag color='green'>Enabled</Tag> : <Tag color='red'>Disabled</Tag>}
              </p>
              <p style={{ color: '#666', marginBottom: 0 }}>
                Use an authenticator app (Google Authenticator, Authy, etc.) to generate one-time codes.
              </p>
            </div>
            {totpEnabled ? (
              <Button type='danger' onClick={this.handleTotpDisableClick}>
                Disable TOTP
              </Button>
            ) : (
              <Button type='primary' onClick={this.handleTotpSetup} loading={isLoading}>
                Enable TOTP
              </Button>
            )}
          </div>
        </Card>

        <Card title='Passkeys (WebAuthn / FIDO2)'>
          <Alert
            message='Passkeys provide passwordless authentication using biometrics or hardware security keys.'
            type='info'
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Button
            type='primary'
            icon='plus'
            onClick={this.handlePasskeyAddClick}
            style={{ marginBottom: 16 }}
          >
            Add Passkey
          </Button>
          <Table
            dataSource={passkeys}
            columns={passkeyColumns}
            rowKey='id'
            loading={isLoading}
            pagination={false}
          />
        </Card>

        {/* TOTP Setup Modal */}
        <Modal
          title='Set Up Two-Factor Authentication'
          visible={this.state.totpSetupModalVisible}
          onOk={this.handleTotpVerify}
          onCancel={() => this.setState({ totpSetupModalVisible: false })}
          okText='Verify & Enable'
          okButtonProps={{ disabled: this.state.verifyCode.length !== 6 }}
        >
          {totpSetupOtpauth && (
            <div style={{ textAlign: 'center' }}>
              <p>Scan this QR code with your authenticator app:</p>
              <QRCodeSVG value={totpSetupOtpauth} size={200} style={{ marginBottom: 16 }} />
              <Divider />
              <p>Or enter this secret manually:</p>
              <code style={{ fontSize: 14, background: '#f5f5f5', padding: '8px 16px', borderRadius: 4 }}>
                {totpSetupSecret}
              </code>
              <Divider />
              <p>Enter the 6-digit code from your app to verify:</p>
              <Input
                placeholder='000000'
                value={this.state.verifyCode}
                onChange={(e) => this.setState({ verifyCode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                style={{ width: 150, textAlign: 'center', fontSize: 20, letterSpacing: 6 }}
                maxLength={6}
              />
            </div>
          )}
        </Modal>

        {/* TOTP Disable Modal */}
        <Modal
          title='Disable Two-Factor Authentication'
          visible={this.state.totpDisableModalVisible}
          onOk={this.handleTotpDisable}
          onCancel={() => this.setState({ totpDisableModalVisible: false })}
          okText='Disable'
          okType='danger'
          okButtonProps={{ disabled: this.state.disableCode.length !== 6 }}
        >
          <Alert
            message='Warning: Disabling 2FA will make your account less secure.'
            type='warning'
            showIcon
            style={{ marginBottom: 16 }}
          />
          <p>Enter your current 6-digit TOTP code to confirm:</p>
          <Input
            placeholder='000000'
            value={this.state.disableCode}
            onChange={(e) => this.setState({ disableCode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
            style={{ width: 150, textAlign: 'center', fontSize: 20, letterSpacing: 6 }}
            maxLength={6}
          />
        </Modal>

        {/* Passkey Add Modal */}
        <Modal
          title='Add New Passkey'
          visible={this.state.passkeyAddModalVisible}
          onOk={this.handlePasskeyAdd}
          onCancel={() => this.setState({ passkeyAddModalVisible: false })}
          okText='Register Passkey'
        >
          <p>Give this passkey a name to help you identify it later:</p>
          <Input
            placeholder='e.g., MacBook Touch ID, YubiKey'
            value={this.state.deviceName}
            onChange={(e) => this.setState({ deviceName: e.target.value })}
          />
          <Alert
            message='When you click "Register Passkey", your browser will prompt you to authenticate.'
            type='info'
            showIcon
            style={{ marginTop: 16 }}
          />
        </Modal>
      </AdminLayout>
    )
  }
}

const mapStateToProps = (state) => ({
  totpEnabled: state.security.totpEnabled,
  passkeyEnabled: state.security.passkeyEnabled,
  passkeys: state.security.passkeys,
  totpSetupSecret: state.security.totpSetupSecret,
  totpSetupOtpauth: state.security.totpSetupOtpauth,
  isLoading: state.security.isLoading,
  error: state.security.error,
})

const mapDispatchToProps = {
  fetchSecurityStatus,
  setupTotp,
  verifyTotp,
  disableTotp,
  fetchPasskeys,
  addPasskey,
  deletePasskey,
}

export default connect(mapStateToProps, mapDispatchToProps)(SecuritySettings)

